import { X, CheckCircle, AlertTriangle, Info, XCircle } from 'lucide-react'
import { useEffect, useState } from 'react'

export type ToastVariant = 'success' | 'error' | 'warning' | 'info'

export interface ToastProps {
    id: string
    title: string
    message: string
    variant?: ToastVariant
    duration?: number
    details?: React.ReactNode
    onClose?: () => void
    isExiting?: boolean
}

const variantStyles = {
    success: {
        icon: CheckCircle,
        iconColor: 'text-emerald-500',
        accentLine: 'bg-emerald-500',
        detailsBg: 'bg-emerald-50 dark:bg-emerald-900/20',
        detailsText: 'text-emerald-700 dark:text-emerald-300',
    },
    error: {
        icon: XCircle,
        iconColor: 'text-rose-500',
        accentLine: 'bg-rose-500',
        detailsBg: 'bg-rose-50 dark:bg-rose-900/20',
        detailsText: 'text-rose-700 dark:text-rose-300',
    },
    warning: {
        icon: AlertTriangle,
        iconColor: 'text-amber-500',
        accentLine: 'bg-amber-500',
        detailsBg: 'bg-amber-50 dark:bg-amber-900/20',
        detailsText: 'text-amber-700 dark:text-amber-300',
    },
    info: {
        icon: Info,
        iconColor: 'text-blue-500',
        accentLine: 'bg-blue-500',
        detailsBg: 'bg-blue-50 dark:bg-blue-900/20',
        detailsText: 'text-blue-700 dark:text-blue-300',
    }
}

export function Toast({
    title,
    message,
    variant = 'info',
    duration = 8000,
    details,
    onClose,
    isExiting = false
}: ToastProps) {
    const styles = variantStyles[variant]
    const Icon = styles.icon

    // Detect dark mode from data-theme attribute
    const [isDark, setIsDark] = useState(false)

    useEffect(() => {
        const checkTheme = () => {
            const theme = document.documentElement.getAttribute('data-theme')
            setIsDark(theme === 'dark')
        }

        checkTheme()

        // Watch for theme changes
        const observer = new MutationObserver(checkTheme)
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['data-theme']
        })

        return () => observer.disconnect()
    }, [])

    return (
        <div
            className={`
                w-full max-w-sm
                transition-all duration-200 ease-out
                ${isExiting
                    ? 'opacity-0 translate-y-2 scale-95'
                    : 'opacity-100 translate-y-0 scale-100'
                }
            `}
        >
            {/* Compact glass card */}
            <div className={`
                relative overflow-hidden
                backdrop-blur-lg
                rounded-lg shadow-lg
                ${isDark
                    ? 'bg-slate-800/90 border border-slate-700/50'
                    : 'bg-white/95 border border-slate-200/80 shadow-slate-200/50'
                }
            `}>
                {/* Left accent line */}
                <div className={`absolute top-0 left-0 bottom-0 w-0.5 ${styles.accentLine}`} />

                {/* Main content - compact horizontal layout */}
                <div className="relative flex items-start gap-2 pl-2.5 pr-1.5 py-2">
                    {/* Icon - minimal, no background */}
                    <Icon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${styles.iconColor}`} />

                    {/* Text content - tight spacing */}
                    <div className="flex-1 min-w-0 space-y-0.5">
                        {/* Title + Message inline when short */}
                        <div className="flex items-baseline gap-1.5 flex-wrap">
                            <span className={`
                                font-semibold text-xs leading-tight
                                ${isDark ? 'text-slate-100' : 'text-slate-800'}
                            `}>
                                {title}
                            </span>
                            <span className={`
                                text-[11px] leading-tight
                                ${isDark ? 'text-slate-400' : 'text-slate-500'}
                            `}>
                                {message}
                            </span>
                        </div>

                        {/* Details section - compact */}
                        {details && (
                            <div className={`
                                mt-1 px-1.5 py-1 rounded text-[10px] leading-snug
                                ${styles.detailsBg} ${styles.detailsText}
                            `}>
                                {details}
                            </div>
                        )}
                    </div>

                    {/* Close button - minimal */}
                    <button
                        onClick={onClose}
                        className={`
                            p-0.5 rounded flex-shrink-0 mt-0.5
                            transition-colors duration-150 cursor-pointer
                            ${isDark
                                ? 'text-slate-500 hover:text-slate-300 hover:bg-slate-700/50'
                                : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
                            }
                        `}
                        aria-label="Close notification"
                    >
                        <X className="w-3 h-3" />
                    </button>
                </div>

                {/* Progress bar - thinner */}
                {duration > 0 && (
                    <div className={`
                        absolute bottom-0 left-0 right-0 h-[2px] overflow-hidden
                        ${isDark ? 'bg-slate-700/50' : 'bg-slate-100'}
                    `}>
                        <div
                            className={`h-full ${styles.accentLine}`}
                            style={{
                                animation: `progress ${duration}ms linear forwards`
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    )
}
